// Real-Time Notification Service for Visitor QR Scans
// Author: MiniMax Agent

export interface VisitorScanAlert {
  id: string;
  campaignId: string;
  campaignName: string;
  visitorType: 'checkin' | 'claim';
  location: string;
  mallName: string;
  shopName: string;
  timestamp: Date;
  scanId: string;
}

export interface NotificationSettings {
  soundEnabled: boolean;
  pushEnabled: boolean;
  visualEnabled: boolean;
  pollingInterval: number; // seconds
}

class NotificationService {
  private subscribers: Array<(alerts: VisitorScanAlert[]) => void> = [];
  private currentAlerts: VisitorScanAlert[] = [];
  private pollingIntervalId: number | null = null;
  private settings: NotificationSettings = {
    soundEnabled: true,
    pushEnabled: false, // Will be enabled after user permission
    visualEnabled: true,
    pollingInterval: 30 // Poll every 30 seconds
  };
  private lastPollTime: Date = new Date(0); // Never polled
  private audio: HTMLAudioElement | null = null;

  constructor() {
    this.initializeAudio();
    this.loadSettings();
    this.loadStoredAlerts();
  }

  private initializeAudio() {
    try {
      // Create a simple beep sound for notifications
      this.audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBjuA2O/BeyoGN4PL8t2QQAkPZrTp5qZOEA');
    } catch (error) {
      console.warn('Audio notification not available:', error);
    }
  }

  private loadSettings() {
    const stored = localStorage.getItem('notification_settings');
    if (stored) {
      this.settings = { ...this.settings, ...JSON.parse(stored) };
    }
  }

  private saveSettings() {
    localStorage.setItem('notification_settings', JSON.stringify(this.settings));
  }

  private loadStoredAlerts() {
    // 🔒 SECURITY FIX: Clear any existing test/demo alerts and start fresh
    // This prevents loading hardcoded "Sandra" alerts or other test data
    console.log('🔄 NotificationService: Starting fresh - clearing any old alert data');
    
    // Clear old alerts from localStorage to prevent fake data persistence
    localStorage.removeItem('visitor_alerts');
    
    // Start with empty alerts array - only real-time database data will be shown
    this.currentAlerts = [];
    
    console.log('✅ NotificationService: Cleared localStorage, starting with fresh alert state');
  }

  private saveAlerts() {
    localStorage.setItem('visitor_alerts', JSON.stringify(this.currentAlerts));
  }

  // Request permission for push notifications
  async requestPushPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      this.settings.pushEnabled = true;
      this.saveSettings();
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      this.settings.pushEnabled = granted;
      this.saveSettings();
      return granted;
    }

    return false;
  }

  // Start polling for new visitor scans
  startPolling(userMallId: number, userShopId: number) {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
    }

    this.pollingIntervalId = window.setInterval(() => {
      this.checkForNewScans(userMallId, userShopId);
    }, this.settings.pollingInterval * 1000);

    // Initial poll
    this.checkForNewScans(userMallId, userShopId);
  }

  // Stop polling
  stopPolling() {
    if (this.pollingIntervalId) {
      clearInterval(this.pollingIntervalId);
      this.pollingIntervalId = null;
    }
  }

  // Check for new visitor scans by querying Supabase database directly
  private async checkForNewScans(userMallId: number, userShopId: number) {
    try {
      console.log('🔍 Checking for new visitor scans...');
      
      // Import Supabase client dynamically to avoid circular dependencies
      const { supabase } = await import('../lib/supabase');
      
      // 🔒 SECURITY: Validate user has access to this mall/shop
      if (!userMallId || !userShopId) {
        console.warn('❌ Security: Missing mall_id or shop_id - skipping scan check');
        return;
      }
      
      console.log('🔒 SECURITY: Fetching scans only for authorized shop:', {
        userMallId,
        userShopId,
        timestamp: new Date().toISOString()
      });
      
      // Query visitor_checkins table for recent scans - FILTERED by user's shop
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      
      const { data: recentScans, error } = await supabase
        .from('visitor_checkins')
        .select('id, qr_mall_id, qr_shop_id, visitor_phone, checkin_time, created_at')
        .eq('qr_mall_id', userMallId)     // 🔒 SECURITY: Only user's mall
        .eq('qr_shop_id', userShopId)     // 🔒 SECURITY: Only user's shop
        .eq('visitor_source', 'qr_scan') // Only QR scans
        .gte('created_at', thirtyMinutesAgo)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) {
        console.warn('Failed to query visitor_checkins:', error);
        return;
      }
      
      if (recentScans && Array.isArray(recentScans)) {
        console.log(`📊 Found ${recentScans.length} authorized QR scans for shop ${userShopId}`);
        
        // 🔒 SECURITY: All scans are already filtered by user authorization
        const userScans = recentScans;
        
        // Debug: Log scan details to verify filtering
        if (recentScans.length > 0) {
          console.log('🔍 DEBUG: Scan filtering verification:', {
            userMallId,
            userShopId,
            scanCount: recentScans.length,
            sampleScans: recentScans.slice(0, 2).map(scan => ({
              scanId: scan.session_id,
              scanMallId: scan.mall_id,
              scanShopId: scan.shop_id,
              source: scan.visitor_source
            }))
          });
        }
        
        console.log(`🎯 Security: ${userScans.length} scans for authorized user only`);
        
        // Convert to alerts format using actual database data
        const newAlerts: VisitorScanAlert[] = await Promise.all(
          userScans.map(async (scan: any) => ({
            id: `${scan.session_id}_${Date.now()}`,
            campaignId: scan.campaign_id || 'general',
            campaignName: await this.getCampaignName(scan),
            visitorType: (scan.qr_checkin_type || 'checkin') as 'checkin' | 'claim',
            location: scan.location || 'Unknown Location', 
            mallName: await this.getMallName(scan.mall_id || userMallId), // 🔒 Use actual mall from DB
            shopName: await this.getShopName(scan.shop_id || userShopId), // 🔒 Use actual shop from DB
            timestamp: new Date(scan.checkin_time),
            scanId: scan.session_id
          }))
        );

        // Add new alerts
        this.addNewAlerts(newAlerts);
      }

      this.lastPollTime = new Date();
      
    } catch (error) {
      console.warn('Failed to check for new scans:', error);
    }
  }

  // Helper methods to get dynamic names from database
  private async getCampaignName(scan: any): Promise<string> {
    const location = scan.qr_location_text || '';
    if (location.includes('barbershop')) return 'Barbershop Campaign';
    if (location.includes('food')) return 'Food Court Promotion';
    if (location.includes('electronics')) return 'Electronics Expo';
    if (location.includes('fashion')) return 'Fashion Week';
    return 'General Check-in';
  }

  private async getMallName(mallId: number): Promise<string> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Try multiple field names based on n8n response format
      const { data: malls, error } = await supabase
        .from('malls')
        .select('mall_name,name')
        .eq('mall_id', mallId);
        
      if (error) {
        console.warn(`Could not find mall name for ID ${mallId}:`, error.message);
        return `Mall ${mallId}`;
      }
      
      if (!malls || malls.length === 0) {
        console.warn(`No mall found for ID ${mallId}`);
        return `Mall ${mallId}`;
      }
      
      const mall = malls[0];
      return mall.mall_name || mall.name || `Mall ${mallId}`;
    } catch (error) {
      console.warn('Error fetching mall name:', error);
      return `Mall ${mallId}`;
    }
  }

  private async getShopName(shopId: number): Promise<string> {
    try {
      const { supabase } = await import('../lib/supabase');
      
      // Try multiple field names based on database structure
      const { data: shops, error } = await supabase
        .from('shops')
        .select('shop_name,name')
        .eq('shop_id', shopId);
        
      if (error) {
        console.warn(`Could not find shop name for ID ${shopId}:`, error.message);
        return `Shop ${shopId}`;
      }
      
      if (!shops || shops.length === 0) {
        console.warn(`No shop found for ID ${shopId}`);
        return `Shop ${shopId}`;
      }
      
      const shop = shops[0];
      return shop.shop_name || shop.name || `Shop ${shopId}`;
    } catch (error) {
      console.warn('Error fetching shop name:', error);
      return `Shop ${shopId}`;
    }
  }

  // Add new alerts and notify subscribers
  private addNewAlerts(alerts: VisitorScanAlert[]) {
    let hasNewAlerts = false;
    
    alerts.forEach(alert => {
      // Check if this alert is new (not already in current alerts)
      if (!this.currentAlerts.some(existing => existing.scanId === alert.scanId)) {
        this.currentAlerts.unshift(alert); // Add to beginning
        hasNewAlerts = true;

        // Play sound if enabled
        if (this.settings.soundEnabled && this.audio) {
          this.audio.play().catch(console.warn);
        }

        // Show push notification if enabled
        if (this.settings.pushEnabled && Notification.permission === 'granted') {
          this.showPushNotification(alert);
        }

        // Show visual notification
        if (this.settings.visualEnabled) {
          this.showVisualNotification(alert);
        }
      }
    });

    // Keep only the last 50 alerts
    if (this.currentAlerts.length > 50) {
      this.currentAlerts = this.currentAlerts.slice(0, 50);
    }

    // Save to localStorage
    this.saveAlerts();

    // Notify subscribers if there are new alerts
    if (hasNewAlerts) {
      this.notifySubscribers();
    }
  }

  // Show browser push notification
  private showPushNotification(alert: VisitorScanAlert) {
    const notification = new Notification('New Visitor Scan!', {
      body: `${alert.visitorType === 'checkin' ? 'Check-in' : 'Claim'} at ${alert.location}`,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `scan-${alert.scanId}`
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  }

  // Show visual toast notification
  private showVisualNotification(alert: VisitorScanAlert) {
    const toast = document.createElement('div');
    toast.className = `
      fixed top-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-sm
      transform transition-all duration-500 ease-in-out translate-x-full
    `;
    
    toast.innerHTML = `
      <div class="flex items-start space-x-3">
        <div class="flex-shrink-0">
          <div class="w-3 h-3 rounded-full ${alert.visitorType === 'checkin' ? 'bg-green-400' : 'bg-blue-400'}"></div>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-900">
            New ${alert.visitorType === 'checkin' ? 'Check-in' : 'Claim'}!
          </p>
          <p class="text-sm text-gray-500">
            ${alert.location} • ${alert.timestamp.toLocaleTimeString()}
          </p>
          <p class="text-xs text-gray-400 mt-1">
            ${alert.campaignName}
          </p>
        </div>
        <button onclick="this.parentElement.parentElement.remove()" class="flex-shrink-0 text-gray-400 hover:text-gray-600">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(toast);

    // Animate in
    setTimeout(() => {
      toast.classList.remove('translate-x-full');
    }, 100);

    // Auto remove after 10 seconds
    setTimeout(() => {
      toast.classList.add('translate-x-full');
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 500);
    }, 10000);
  }

  // Subscribe to alerts (for React components)
  subscribe(callback: (alerts: VisitorScanAlert[]) => void) {
    this.subscribers.push(callback);
    
    // Immediately call with current alerts
    callback(this.currentAlerts);
    
    // Return unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  // Notify all subscribers
  private notifySubscribers() {
    this.subscribers.forEach(callback => {
      try {
        callback(this.currentAlerts);
      } catch (error) {
        console.error('Error in notification subscriber:', error);
      }
    });
  }

  // Get current alerts
  getAlerts(): VisitorScanAlert[] {
    return [...this.currentAlerts];
  }

  // Mark alerts as read
  markAsRead(alertIds: string[]) {
    this.currentAlerts = this.currentAlerts.filter(alert => !alertIds.includes(alert.id));
    this.saveAlerts();
    this.notifySubscribers();
  }

  // Clear all alerts
  clearAllAlerts() {
    console.log('🗑️ NotificationService: Clearing all alerts');
    this.currentAlerts = [];
    localStorage.removeItem('visitor_alerts'); // Also clear from localStorage
    this.notifySubscribers();
  }

  // Force refresh alerts (useful for testing)
  refreshAlerts() {
    console.log('🔄 NotificationService: Forcing alert refresh');
    this.clearAllAlerts();
    // This will trigger a fresh fetch on next polling cycle
  }

  // Update notification settings
  updateSettings(newSettings: Partial<NotificationSettings>) {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    
    // If push is being enabled, request permission
    if (newSettings.pushEnabled === true && !this.settings.pushEnabled) {
      this.requestPushPermission();
    }
  }

  // Get current settings
  getSettings(): NotificationSettings {
    return { ...this.settings };
  }
}

// Create singleton instance
export const notificationService = new NotificationService();
